<template lang="pug">
  .review(itemprop="review" itemscope itemtype="http://schema.org/Review").xs-f-small.md-f-medium.max-at-tweak
    meta(itemprop="itemReviewed" :content="productName")
    .hide(itemprop="reviewRating" itemscope itemtype="http://schema.org/Rating")
      span(itemprop="ratingValue") {{ review.rating }}
      span(itemprop="bestRating") 5

    .review-info
      .name-info
        stars(:rating="review.rating")
        p.text-color-2(itemprop="author") {{ review.name }}
        template(v-if="review.city && review.state")
          p.capitalize.text-color-2 Location: {{ location }}
        p.name-info-label.text-color-2(itemprop="datePublished" :content="$formatDate(review.review_date, 'YYYY-MM-D')") {{ $formatDate(review.review_date, 'MMM D, YYYY') }}

        .extra-info.xs-mt-100m.xs-mb-175m.md-mb-100m
          p.text-color-2.capitalize(v-if="review.age_range") Age range: {{ filterLabel('ageRange', review.age_range) }}
          p.text-color-2.capitalize(v-if="review.hair_type") Hair type: {{ filterLabel('hairTypes', review.hair_type) }}
          p.text-color-2.capitalize(v-if="review.gray_amount") Amount of gray: {{ filterLabel('grayAmount', review.gray_amount) }}

      .comment
        h4.comment-title(itemprop="name" v-html="review.title" data-mr-ass="review-title")
        p(show-more="200" v-html="review.comment")

        .images(v-if="review.images && review.images.length")
          .review-image(v-for="(image, index) in review.images")
            img(:src="image.url" :alt="image.alt_text" loading="lazy")
            button.btn-reset.magnify(@click="() => showZoomModal(index)" ref="modalButton")
              span.ada-tooltip Open swatch modal
              mr-icon(name="zoom-in")

        template(v-if="badgeText")
          .badge
            span.icon-check.xs-f-large.max-at-tweak.text-color-2
            span {{ badgeText }}

        template(v-if='showResponse')
          .public-response
            TruncatedText(:text="review.public_response")

            p.xs-f-medium.max-at-tweak.text-color-2.xs-mt-100m Answered by a Licensed Colorist
              template(v-if="answeredDaysAgo")
                span {{ answeredDaysAgo }}

        .helpful
          p(v-if="voted" role="alert").voted
            span.icon-check-nobox Thank you for your feedback!
          p(v-else)
            span Was this helpful?
            button.helpful-btn.yes.btn-reset.brand-color-1(type="button" @click="voteOnReview(true)" data-mr-ass="helpful-yes")
              span.underline Yes&nbsp;
              span.icon-arrow-forward
            button.helpful-btn.no.btn-reset.brand-color-1(type="button" @click="voteOnReview(false)" data-mr-ass="helpful-no")
              span.underline No&nbsp;
              span.icon-arrow-forward

</template>

<script>
  import dayjs from '@dayjs';
  import { mapState, mapGetters, mapActions } from 'vuex';
  import vueReviewSvc from '@services/vueReviewSvc';
  import TruncatedText from '../../../TruncatedText/TruncatedText';

  export default {
    name: 'Review',

    components: { TruncatedText },

    props: {
      review: {
        type: Object,
        required: true,
      },

      productName: {
        type: String,
        required: true,
      },
    },

    data() {
      return {
        voted: false
      };
    },

    computed: {
      ...mapState('cms', ['cmsSettings']),
      ...mapGetters('reviews', ['filterLabel']),

      badgeText() {
        let text = null;

        if (this.review.sponsored) {
          text = 'Sponsored review';
        } else if (this.review.invoice_id) {
          text = 'Verified reviewer';
        }

        return text;
      },

      showResponse() {
        return Boolean(this.cmsSettings.showReviewPublicResponse && this.review.public_response);
      },

      answeredDaysAgo() {
        let text = null;
        let reviewAge = dayjs().diff(this.review.review_date, 'days');
        //- the "X days ago" copy should ONLY show if it was within the last 30 days and if the review is older than 60 days
        if (reviewAge > 60 && this.review.public_response_date) {
          let responseAge = dayjs().diff(this.review.public_response_date, 'days');

          if (responseAge == 0) {
            text = ', today';
          } else if (responseAge < 30) {
            text = `, ${responseAge} day${responseAge == 1 ? '' : 's'} ago`;
          }
        }

        return text;
      },

      location() {
        return `${this.review.city}, ${this.review.state}`;
      },
    },

    methods: {
      voteOnReview(helpful) {
        this.voted = true;

        let payload = {
          helpful,
          reviewId: this.review.id,
          productId: this.review.product_id,
        };

        vueReviewSvc.voteForReview(payload);
      },

      showZoomModal(index) {
        this.showModal({
          component: 'ImageCarouselModal',
          theme: 'image-carousel',
          props: {
            title: this.review.name,
            subtitle: this.location,
            images: this.review.images,
            index,
          },
        });
      },
      ...mapActions('modal', ['showModal']),
    },
  };
</script>

<style scoped lang="stylus">
  .review
    margin-bottom 2em

    .name-info
      width 70%

    .capitalize
      text-transform capitalize

    .comment-title
      font-family f-primary-bold
      font-size 1.2em
      margin-bottom 0.8em

    .comment
      margin-top 1em

      .helpful-btn
        margin 0
        padding 0.25em 0.25em
        margin-right 0.25em
        width 4.25em
        display inline-block
        text-align center
        background-color #fff

        .icon-arrow-forward
          margin 0

        &.yes
          .icon-arrow-forward
            &:before
              transform(rotate(-90deg))

        &.no
          .icon-arrow-forward
            &:before
              transform(rotate(90deg))


    .public-response
      padding 0.3em 0 0.5em 1em
      border-left 3px solid color-pale-blue

    .images
      padding 1em 0
      display flex
      flex-direction row
      width 100%

      .review-image
        position relative
        margin-right 1em
        button.magnify
          position absolute
          font-size 1.3em
          top 0
          right 0
          padding 0.2em 0.4em
          color #fff
        img
          vertical-align top
          max-width 8.5em
          max-height 8.5em
          margin-bottom 1em

    .badge
      margin 1em 0

    .stars-right
      position absolute
      top 0
      right 1.5em
      padding-top 0.3em
      margin-bottom 0.7em

    .helpful
      margin-top 0.9em
      span
        margin-right 5px
        line-height 36px

      button
        margin 0 3px

    @media mq-tablet-plus
      .name-info
        display inline-block
        width 29%
        vertical-align top

      .comment
        display inline-block
        width 70%
        vertical-align top
        margin-top 0px
</style>
